/**
 * Tasks list state ⇄ URL query string.
 *
 * The type/status/priority filters, the sort and the page were `useState`, so a
 * reload, a browser Back, or a link shared with a teammate all silently dropped
 * whatever the user had narrowed to. The URL is the source of truth instead
 * (WorkflowsPage precedent), which also makes the fetch a pure function of the
 * location.
 *
 * Two rules keep the URL honest:
 *  - **Defaults delete their param.** `?type=&page=1` and `` describe the same
 *    view; writing only the non-default ones means the two spellings can never
 *    diverge and the default URL stays clean.
 *  - **Garbage resolves to the default.** Every value is checked against the
 *    dropdown that produces it, so a hand-edited `?status=lol` renders the
 *    unfiltered list instead of asking the API to filter on nonsense.
 */

import { PRIORITY_OPTIONS, STATUS_OPTIONS, TASK_TYPE_OPTIONS, type EnumOption } from './taskLabels'

export type SortDir = 'asc' | 'desc'

/** The two columns the API can order by (`sort_by` in routes/tasks.py). */
export type TaskSortField = 'created_at' | 'due_date'

/**
 * Where each column starts when you first click it.
 *
 * They differ on purpose. A creation time is a feed — the newest task is the
 * one you have not seen, so it belongs on top. A due date is a deadline — the
 * soonest one is the urgent one, so it belongs on top. Defaulting both to the
 * same direction would make one of the two columns useless on first click.
 */
export const DEFAULT_SORT_DIR: Record<TaskSortField, SortDir> = {
  created_at: 'desc',
  due_date: 'asc',
}

export interface TaskListParams {
  task_type: string
  status: string
  priority: string
  sort: TaskSortField
  dir: SortDir
  /** 0-based here, 1-based in the URL — see `applyTaskListPatch`. */
  page: number
}

export const TASK_LIST_DEFAULTS: TaskListParams = {
  task_type: '',
  status: '',
  priority: '',
  sort: 'created_at',
  dir: DEFAULT_SORT_DIR.created_at,
  page: 0,
}

/** URL param name per state key. Short names because they show up in shared links. */
export const TASK_PARAM_KEYS: Record<keyof TaskListParams, string> = {
  task_type: 'type',
  status: 'status',
  priority: 'priority',
  sort: 'sort',
  dir: 'dir',
  page: 'page',
}

/** Pre-`sort` links spelled the due-date direction as `?due=`. Kept as a read-only
 *  alias so those links still land on the view they described, instead of being
 *  silently reinterpreted as a direction for the new default column. */
const LEGACY_DUE_KEY = 'due'

/** Keys whose change invalidates the current page — page 3 of the old result set
 *  is meaningless once the result set changes (the sort included: reordering
 *  makes "page 3" a different 25 rows). */
const PAGE_INVALIDATING_KEYS = ['task_type', 'status', 'priority', 'sort', 'dir'] as const

/** A filter value is only accepted if the dropdown that sets it offers it. */
function pickEnum(raw: string | null | undefined, options: EnumOption[]): string {
  const value = String(raw ?? '')
  return options.some(o => o.value !== '' && o.value === value) ? value : ''
}

function pickSort(raw: string | null | undefined): TaskSortField {
  return raw === 'due_date' ? 'due_date' : 'created_at'
}

function pickDir(raw: string | null | undefined, sort: TaskSortField): SortDir {
  if (raw === 'asc' || raw === 'desc') return raw
  return DEFAULT_SORT_DIR[sort]
}

/** Missing / non-numeric / zero / negative / fractional all resolve to page 0. */
function pickPage(raw: string | null | undefined): number {
  const n = Number(raw)
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) - 1 : 0
}

export function parseTaskListParams(search: URLSearchParams): TaskListParams {
  const rawSort = search.get(TASK_PARAM_KEYS.sort)
  const legacyDue = search.get(LEGACY_DUE_KEY)
  // `?due=` without `?sort=` is an old link: it meant due-date ordering, and the
  // value it carries is that column's direction.
  const usingLegacy = rawSort === null && legacyDue !== null
  const sort = usingLegacy ? 'due_date' : pickSort(rawSort)
  return {
    task_type: pickEnum(search.get(TASK_PARAM_KEYS.task_type), TASK_TYPE_OPTIONS),
    status: pickEnum(search.get(TASK_PARAM_KEYS.status), STATUS_OPTIONS),
    priority: pickEnum(search.get(TASK_PARAM_KEYS.priority), PRIORITY_OPTIONS),
    sort,
    dir: pickDir(usingLegacy ? legacyDue : search.get(TASK_PARAM_KEYS.dir), sort),
    page: pickPage(search.get(TASK_PARAM_KEYS.page)),
  }
}

/**
 * Merge a partial change into the current query string.
 *
 * Params we don't own (anything another feature put on the URL) survive
 * untouched. Changing a filter or the sort resets to page 1 unless the caller
 * asked for a specific page in the same patch — otherwise flipping a filter
 * while on page 4 requests an offset the narrowed result set doesn't have and
 * the table comes back empty for no visible reason.
 *
 * Patching `sort` alone (clicking a different column) takes that column's
 * natural direction rather than carrying the previous column's over — clicking
 * "Due" after reading newest-first should give soonest-due, not latest-due.
 */
export function applyTaskListPatch(
  search: URLSearchParams,
  patch: Partial<TaskListParams>,
): URLSearchParams {
  const current = parseTaskListParams(search)
  const sort = pickSort(patch.sort ?? current.sort)
  const switchedColumn = patch.sort !== undefined && pickSort(patch.sort) !== current.sort
  const next: TaskListParams = {
    // Re-validated rather than trusted: a patch is just as capable of carrying a
    // stale or bogus value as the URL is.
    task_type: pickEnum(patch.task_type ?? current.task_type, TASK_TYPE_OPTIONS),
    status: pickEnum(patch.status ?? current.status, STATUS_OPTIONS),
    priority: pickEnum(patch.priority ?? current.priority, PRIORITY_OPTIONS),
    sort,
    dir: pickDir(patch.dir ?? (switchedColumn ? undefined : current.dir), sort),
    page: Math.max(0, Math.floor(patch.page ?? current.page) || 0),
  }
  if (patch.page === undefined && PAGE_INVALIDATING_KEYS.some(key => patch[key] !== undefined)) {
    next.page = 0
  }

  const out = new URLSearchParams(search)
  // The alias is read-only: once anything is patched the URL speaks the current
  // spelling, so a stale `?due=` cannot sit alongside `?sort=` contradicting it.
  out.delete(LEGACY_DUE_KEY)
  const write = (key: string, value: string, fallback: string) => {
    if (value === fallback) out.delete(key)
    else out.set(key, value)
  }
  write(TASK_PARAM_KEYS.task_type, next.task_type, TASK_LIST_DEFAULTS.task_type)
  write(TASK_PARAM_KEYS.status, next.status, TASK_LIST_DEFAULTS.status)
  write(TASK_PARAM_KEYS.priority, next.priority, TASK_LIST_DEFAULTS.priority)
  write(TASK_PARAM_KEYS.sort, next.sort, TASK_LIST_DEFAULTS.sort)
  // Compared against THIS column's default, not the list default, so
  // `?sort=due_date` alone still means "due date, soonest first".
  write(TASK_PARAM_KEYS.dir, next.dir, DEFAULT_SORT_DIR[next.sort])
  write(TASK_PARAM_KEYS.page, String(next.page + 1), String(TASK_LIST_DEFAULTS.page + 1))
  return out
}

/**
 * Drop the filters (and the page they were paged into), keeping the sort.
 *
 * The sort is a reading preference, not a narrowing: "clear filters" promises
 * to show everything, not to silently reorder what the user chose. Page goes
 * because clearing filters usually widens the result set and page 4 of the old
 * one is nobody's intended landing spot.
 */
export function clearTaskListFilters(search: URLSearchParams): URLSearchParams {
  const out = new URLSearchParams(search)
  out.delete(TASK_PARAM_KEYS.task_type)
  out.delete(TASK_PARAM_KEYS.status)
  out.delete(TASK_PARAM_KEYS.priority)
  out.delete(TASK_PARAM_KEYS.page)
  return out
}

/** True when the list is narrowed — drives the "nothing matched *these filters*"
 *  empty state. The sort is excluded on purpose: reordering can't hide a row. */
export function hasActiveTaskFilters(params: TaskListParams): boolean {
  return Boolean(params.task_type || params.status || params.priority)
}

/** Mirrors `tasksApi.getTasks`'s param shape (not a loose Record) so a typo in a
 *  key or a bad sort direction fails at compile time. */
export interface TaskListQuery {
  status?: string
  priority?: string
  task_type?: string
  sort_by: TaskSortField
  sort_dir: SortDir
  limit: number
  offset: number
}

/**
 * The `/tasks` query for this view. Empty filters are omitted rather than sent
 * as `''`, which the API would otherwise have to interpret.
 */
export function buildTaskListQuery(params: TaskListParams, pageSize: number): TaskListQuery {
  const query: TaskListQuery = {
    limit: pageSize,
    offset: Math.max(0, params.page) * pageSize,
    sort_by: params.sort,
    sort_dir: params.dir,
  }
  if (params.task_type) query.task_type = params.task_type
  if (params.status) query.status = params.status
  if (params.priority) query.priority = params.priority
  return query
}
